import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, interval, Subscription } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PlatformService } from './platform.service';
import { AuthService } from './auth.service';

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export type NotificationType = 'review' | 'comment_upvote' | 'new_match';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  body: string;
  data?: Record<string, any>;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface NotificationsResponse {
  items: Notification[];
  pagination: NotificationPagination;
}

export interface NotificationStats {
  total: number;
  unread: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly platform = inject(PlatformService);
  private readonly authService = inject(AuthService);
  private readonly apiUrl = `${environment.apiUrl}/notifications`;

  // Polling
  private pollSubscription: Subscription | null = null;
  private readonly POLL_INTERVAL = 45_000; // 45 seconds

  // Reactive state
  private readonly _unreadCount = signal<number>(0);
  private readonly _stats = signal<NotificationStats | null>(null);

  /** Unread notification count (for badge display) */
  readonly unreadCount = this._unreadCount.asReadonly();

  /** Whether there are unread notifications */
  readonly hasUnread = computed(() => this._unreadCount() > 0);

  /** Full stats object */
  readonly stats = this._stats.asReadonly();

  constructor() {
    // Start polling when authenticated
    if (this.platform.isBrowser) {
      this.startPolling();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // API Methods
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Get paginated list of notifications
   */
  getNotifications(page = 1, limit = 20, unreadOnly = false): Observable<NotificationsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (unreadOnly) {
      params = params.set('unreadOnly', 'true');
    }

    return this.http.get<NotificationsResponse>(this.apiUrl, { params, withCredentials: true }).pipe(
      catchError(error => {
        console.error('Failed to fetch notifications:', error);
        return of({ items: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } });
      })
    );
  }

  /**
   * Get notification stats (total + unread count)
   */
  getStats(): Observable<NotificationStats> {
    return this.http.get<NotificationStats>(`${this.apiUrl}/stats`, { withCredentials: true }).pipe(
      tap(stats => {
        this._stats.set(stats);
        this._unreadCount.set(stats.unread);
      }),
      catchError(error => {
        console.error('Failed to fetch notification stats:', error);
        return of({ total: 0, unread: 0 });
      })
    );
  }

  /**
   * Mark a single notification as read
   */
  markAsRead(notificationId: string): Observable<{ success: boolean }> {
    return this.http.patch<{ success: boolean }>(
      `${this.apiUrl}/${notificationId}/read`,
      {},
      { withCredentials: true }
    ).pipe(
      tap(() => {
        // Decrement unread count optimistically
        this._unreadCount.update(count => Math.max(0, count - 1));
      }),
      catchError(error => {
        console.error('Failed to mark notification as read:', error);
        return of({ success: false });
      })
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<{ success: boolean; message?: string }> {
    return this.http.patch<{ success: boolean; message?: string }>(
      `${this.apiUrl}/read-all`,
      {},
      { withCredentials: true }
    ).pipe(
      tap(() => {
        this._unreadCount.set(0);
      }),
      catchError(error => {
        console.error('Failed to mark all notifications as read:', error);
        return of({ success: false });
      })
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Polling
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Start polling for unread count
   */
  startPolling(): void {
    this.stopPolling();

    // Initial fetch
    this.refreshStats();

    // Poll periodically
    this.pollSubscription = interval(this.POLL_INTERVAL).subscribe(() => {
      if (this.authService.isAuthenticated() && !this.authService.isAnonymous()) {
        this.refreshStats();
      }
    });
  }

  /**
   * Stop polling
   */
  stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }

  /**
   * Manually refresh stats (e.g., after receiving a push notification)
   */
  refreshStats(): void {
    if (!this.authService.isAuthenticated() || this.authService.isAnonymous()) {
      this._unreadCount.set(0);
      this._stats.set(null);
      return;
    }
    this.getStats().subscribe();
  }

  /**
   * Get notification icon based on type
   */
  getNotificationIcon(type: NotificationType): string {
    switch (type) {
      case 'review':
        return 'assets/icons/shield-check.svg';
      case 'comment_upvote':
        return 'assets/icons/thumbs-up.svg';
      case 'new_match':
        return 'assets/icons/handshake.svg';
      default:
        return 'assets/icons/bell.svg';
    }
  }

  /**
   * Format relative time for notification display
   */
  getRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin}m`;
    if (diffHour < 24) return `Hace ${diffHour}h`;
    if (diffDay < 7) return `Hace ${diffDay}d`;
    if (diffDay < 30) return `Hace ${Math.floor(diffDay / 7)}sem`;
    return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
  }
}
